import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import {
  SubmitHandlerWithDirtyFields,
  buildDirtyObject,
} from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { removeEmptyFields } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/remove-empty-fields/remove-empty-fields';
import { getSetChargePointsTariffDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/set-charge-points-tariff.form-default-values';
import { SetChargePointsTariffModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/set-charge-points-tariff.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useCpmsBoSetChargePointsTariffMutation } from '@app/core/service/cpms-bo-charge-point.service';

import {
  UpdateChargePointTariffContextProvider,
  useChargePointContext,
} from '../charge-point.context';
import { ChargePointTariffUpdateFormController } from './charge-point-tariff-update-form/charge-point-tariff-update-form.controller';

interface ChargePointTariffUpdateControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const ChargePointTariffUpdateController = ({
  onCancel,
  onSuccess,
}: ChargePointTariffUpdateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [updateChargePointTariffMutation] =
    useCpmsBoSetChargePointsTariffMutation();
  const chargePoint = useChargePointContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<
    Pick<SetChargePointsTariffModel, 'chargePointIds' | 'tariffId'>
  > = (errors) => {
    Logger.error(errors);
    toast.error(
      t('$.charge-point-tariff-update.error', {
        error: t('global.unknown'),
      }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    Pick<SetChargePointsTariffModel, 'chargePointIds' | 'tariffId'>
  > = (fieldValues, _, dirtyFields) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    const sanitizedObject = removeEmptyFields(
      buildDirtyObject(dirtyFields, fieldValues),
    );

    if (!sanitizedObject.tariffId) {
      setMultiStepFormState(MultiStepFormState.Default);
      return;
    }

    updateChargePointTariffMutation({
      chargePointIds: fieldValues.chargePointIds,
      tariffId: sanitizedObject.tariffId,
    })
      .unwrap()
      .then(() => {
        toast.success(t('$.charge-point-tariff-update.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(
          t('$.charge-point-tariff-update.error', {
            error: errorMessage,
          }),
        );
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getSetChargePointsTariffDefaultValues(chargePoint);

  return (
    <UpdateChargePointTariffContextProvider data={data}>
      <ChargePointTariffUpdateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </UpdateChargePointTariffContextProvider>
  );
};
